import { Clock, Globe, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { CONTACT } from "../../lib/data";

const CARDS = [
  { icon: Phone, label: "Call Us", value: CONTACT.phone, href: CONTACT.phoneHref, testId: "contact-call-card" },
  { icon: Phone, label: "Office Landline", value: CONTACT.landline, href: CONTACT.landlineHref, testId: "contact-landline-card" },
  { icon: MessageCircle, label: "WhatsApp", value: "Chat with an expert", href: CONTACT.whatsapp, testId: "contact-whatsapp-card" },
  { icon: Mail, label: "Email", value: CONTACT.email, href: CONTACT.emailHref, testId: "contact-email-card" },
  { icon: Globe, label: "Website", value: CONTACT.website, href: CONTACT.websiteHref, testId: "contact-website-card" },
  { icon: MapPin, label: "Visit Us", value: "Akshar Business Park, Vashi", href: CONTACT.mapsUrl, testId: "contact-address-card" },
];

export const ContactSection = () => (
  <section id="contact" className="border-t border-gray-200 bg-fog py-24 md:py-32" data-testid="contact-section">
    <div className="mx-auto max-w-7xl px-5 md:px-8">
      <SectionHeading
        overline="Contact Us"
        title="Let's Start the Conversation"
        sub="Reach out through any channel — our advisory team responds during business hours."
        testId="contact-heading"
      />

      <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {CARDS.map((c, i) => {
          const Icon = c.icon;
          return (
            <Reveal key={c.label} delay={i * 0.07} y={26} className="h-full">
              <a
                href={c.href}
                target={c.href.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                data-testid={c.testId}
                className="glow-hover group block h-full rounded-xl border border-gray-200 bg-white p-7 shadow-[0_2px_10px_rgba(16,20,24,0.04)]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-gold-line bg-gold-tint text-gold-deep">
                  <Icon size={19} strokeWidth={1.6} />
                </span>
                <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.22em] text-gray-400">{c.label}</p>
                <p className="mt-2 font-heading text-base font-semibold text-navy transition-colors duration-300 group-hover:text-orange-deep md:text-lg">
                  {c.value}
                </p>
              </a>
            </Reveal>
          );
        })}
      </div>

      <div className="mt-14 grid gap-4 lg:grid-cols-3">
        <Reveal className="lg:col-span-2">
          <div
            className="flex h-full min-h-[380px] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_2px_10px_rgba(16,20,24,0.04)]"
            data-testid="office-map"
          >
            <iframe
              title="LendX Office — Akshar Business Park, Vashi, Navi Mumbai"
              src={CONTACT.mapsEmbed}
              className="min-h-[280px] w-full flex-1 border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="office-map-iframe"
            />
            <div className="flex flex-col gap-3 border-t border-gray-200 p-5 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="mt-0.5 shrink-0 text-gold-deep" />
                <p className="text-sm leading-relaxed text-gray-700" data-testid="office-address">{CONTACT.address}</p>
              </div>
              <a
                href={CONTACT.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="get-directions-btn"
                className="shrink-0 rounded-full bg-gold px-5 py-2.5 text-center text-xs font-bold text-navy-deep transition-transform duration-300 hover:scale-105"
              >
                Get Directions
              </a>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="flex h-full min-h-[300px] flex-col justify-between rounded-2xl border border-gray-200 bg-white p-8 shadow-[0_2px_10px_rgba(16,20,24,0.04)]" data-testid="business-hours-card">
            <div>
              <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-gold-line bg-gold-tint text-gold-deep">
                <Clock size={19} />
              </span>
              <h3 className="mt-6 font-heading text-xl font-semibold text-navy">Business Hours</h3>
              <p className="mt-3 text-sm leading-relaxed text-gray-600">{CONTACT.hours}</p>
              <p className="mt-2 text-sm leading-relaxed text-gray-400">Sunday — Closed</p>
            </div>
            <a
              href={CONTACT.phoneHref}
              data-testid="hours-call-btn"
              className="mt-8 flex items-center justify-center gap-2 rounded-full border border-gray-300 py-3 text-sm font-semibold text-gray-800 transition-colors duration-300 hover:border-orange hover:text-orange-deep"
            >
              <Phone size={15} /> Click to Call
            </a>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
